import { useEffect, useState } from 'react';

export type ResolvedTheme =
  | 'light'
  | 'dark'
  | 'synthwave84'
  | 'solarized-light'
  | 'solarized-dark'
  | 'dracula'
  | 'nord'
  | 'gruvbox-dark'
  | 'gruvbox-light'
  | 'tokyo-night'
  | 'catppuccin-mocha'
  | 'catppuccin-macchiato'
  | 'catppuccin-latte'
  | 'rose-pine'
  | 'monokai'
  | 'venice'
  | 'venetian-night';

export type ThemeChoice = ResolvedTheme | 'auto';

export const LOCAL_THEME_CACHE_KEY = 'rialto.theme';

function getResolvedTheme(): ResolvedTheme {
  const t = document.documentElement.dataset.theme as ResolvedTheme | undefined;
  return t ?? 'light';
}

// Resolve the user-chosen `auto` to a concrete theme and apply it to <html>.
// Also caches the choice in localStorage so theme-init.js can avoid the flash
// on the next pre-paint.
export function applyTheme(choice: ThemeChoice): void {
  const resolved: ResolvedTheme =
    choice === 'auto'
      ? matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : choice;
  document.documentElement.dataset.theme = resolved;
  try {
    localStorage.setItem(LOCAL_THEME_CACHE_KEY, choice);
  } catch {
    // localStorage may be disabled — the in-DOM data-theme still takes effect.
  }
}

export function useResolvedTheme(): ResolvedTheme {
  const [theme, setTheme] = useState(getResolvedTheme);
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setTheme(getResolvedTheme()));
    obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return theme;
}

interface Retint {
  bgS: number;
  bgL: number;
  fgS: number;
  fgL: number;
}

// Per-theme HSL targets for event tiles. The Google calendar's hue is kept;
// saturation and lightness are slammed to these values so a "red calendar"
// stays the red one but adopts the active theme's aesthetic. fgL flips
// dark-on-light vs light-on-dark per theme.
const RETINTS: Record<ResolvedTheme, Retint> = {
  light:              { bgS: 65, bgL: 50, fgS: 0,  fgL: 100 },
  dark:               { bgS: 55, bgL: 45, fgS: 10, fgL: 96  },
  synthwave84:        { bgS: 85, bgL: 58, fgS: 5,  fgL: 98  },
  'solarized-light':  { bgS: 45, bgL: 72, fgS: 25, fgL: 22  },
  'solarized-dark':   { bgS: 50, bgL: 42, fgS: 20, fgL: 94  },
  dracula:            { bgS: 60, bgL: 60, fgS: 25, fgL: 18  },
  nord:               { bgS: 35, bgL: 52, fgS: 15, fgL: 96  },
  'gruvbox-dark':     { bgS: 50, bgL: 42, fgS: 30, fgL: 90  },
  'gruvbox-light':    { bgS: 55, bgL: 65, fgS: 35, fgL: 20  },
  'tokyo-night':      { bgS: 50, bgL: 50, fgS: 20, fgL: 96  },
  'catppuccin-mocha': { bgS: 45, bgL: 65, fgS: 30, fgL: 16  },
  'catppuccin-macchiato': { bgS: 45, bgL: 64, fgS: 30, fgL: 16 },
  'catppuccin-latte': { bgS: 50, bgL: 72, fgS: 30, fgL: 24  },
  'rose-pine':        { bgS: 40, bgL: 50, fgS: 20, fgL: 96  },
  monokai:            { bgS: 55, bgL: 48, fgS: 15, fgL: 96  },
  // Venice: jewel-toned event tiles like enamelled Murano glass on cream stone.
  venice:             { bgS: 50, bgL: 60, fgS: 30, fgL: 22  },
  // Venetian Night: candlelit-stained-glass tiles glowing on dark canal water.
  'venetian-night':   { bgS: 60, bgL: 48, fgS: 15, fgL: 94  }
};

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp < 1)      { r1 = c; g1 = x; }
  else if (hp < 2) { r1 = x; g1 = c; }
  else if (hp < 3) { g1 = c; b1 = x; }
  else if (hp < 4) { g1 = x; b1 = c; }
  else if (hp < 5) { r1 = x; b1 = c; }
  else             { r1 = c; b1 = x; }
  const m = ln - c / 2;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255)
  };
}

function relativeLuminance(r: number, g: number, b: number): number {
  const norm = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * norm(r) + 0.7152 * norm(g) + 0.0722 * norm(b);
}

function contrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const m = hex.trim().match(/^#?([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (!m) return null;
  const raw = m[1];
  let r: number, g: number, b: number;
  if (raw.length === 3) {
    r = parseInt(raw[0] + raw[0], 16);
    g = parseInt(raw[1] + raw[1], 16);
    b = parseInt(raw[2] + raw[2], 16);
  } else {
    r = parseInt(raw.slice(0, 2), 16);
    g = parseInt(raw.slice(2, 4), 16);
    b = parseInt(raw.slice(4, 6), 16);
  }
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

// Memoise re-tinted colors: hexToHsl is regex+parse, and the calendar palette
// is tiny (~10 unique hexes) so a single Map covers every event chip/bar.
const bgCache = new Map<string, string>();
const fgCache = new Map<string, string>();

export function retintEventBackground(hex: string | null, theme: ResolvedTheme): string {
  if (!hex) return 'var(--accent)';
  const key = `${theme}:${hex}`;
  const cached = bgCache.get(key);
  if (cached) return cached;
  const hsl = hexToHsl(hex);
  if (!hsl) {
    bgCache.set(key, hex);
    return hex;
  }
  const cfg = RETINTS[theme] ?? RETINTS.light;
  const out = `hsl(${hsl.h} ${cfg.bgS}% ${cfg.bgL}%)`;
  bgCache.set(key, out);
  return out;
}

export function retintEventForeground(hex: string | null, theme: ResolvedTheme): string {
  if (!hex) return 'var(--accent-fg)';
  const key = `${theme}:${hex}`;
  const cached = fgCache.get(key);
  if (cached) return cached;
  const hsl = hexToHsl(hex);
  if (!hsl) {
    fgCache.set(key, 'var(--accent-fg)');
    return 'var(--accent-fg)';
  }
  const cfg = RETINTS[theme] ?? RETINTS.light;
  // Theme picks an fg lightness, but the bg's actual luminance varies by hue
  // (yellow at L=50 is far brighter than blue at L=50). Check the theme default
  // against the bg; if contrast fails WCAG AA, flip to whichever extreme works.
  const bgRgb = hslToRgb(hsl.h, cfg.bgS, cfg.bgL);
  const bgLum = relativeLuminance(bgRgb.r, bgRgb.g, bgRgb.b);
  const fgRgb = hslToRgb(hsl.h, cfg.fgS, cfg.fgL);
  const fgLum = relativeLuminance(fgRgb.r, fgRgb.g, fgRgb.b);
  let fgL = cfg.fgL;
  if (contrastRatio(bgLum, fgLum) < 4.5) {
    fgL = bgLum > 0.179 ? 12 : 96;
  }
  const out = `hsl(${hsl.h} ${cfg.fgS}% ${fgL}%)`;
  fgCache.set(key, out);
  return out;
}
